import { Alert, Box, CircularProgress, IconButton, Snackbar, Stack, Typography } from '@mui/material'
import React, { useEffect, useState, useRef } from 'react' // Import useRef
import { FixedBottomBar, HeaderBox } from './MobileBookPage'
import { useLocation, useNavigate } from 'react-router-dom'
import { useDownloadApp } from '../../Contexts/DownloadAppContext';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import { useTranslation } from 'react-i18next';
import DivineQuotesService from '../../Services/DivineQuotes';
import DownloadIcon from '@mui/icons-material/Download';
import ShareIcon from '@mui/icons-material/Share';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const DivineQuotesImageview = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { setShowDownloadPopup } = useDownloadApp();
  const FIXED_BOTTOM_BAR_HEIGHT = 56;
  const [loading, setLoading] = useState(true);
  const [divineQuotes, setDivineQuotes] = useState<any | undefined[]>([]);
  const location: any = useLocation();
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(location?.state?.currentImgIndex || 0);

  // State for Snackbar
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error" | "info" | "warning">("success");

  // Refs for swipe
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const minSwipeDistance = 50; 

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await DivineQuotesService.getDivineQuotes("", 0, 3000);
        setDivineQuotes(response.result?.items || []);
      } catch (error) {
        console.error("Error fetching divine quotes:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const showSnackbar = (message: string, severity: "success" | "error" | "info" | "warning") => {
    setSnackbarOpen(false);
    setTimeout(() => {
      setSnackbarMessage(message);
      setSnackbarSeverity(severity);
      setSnackbarOpen(true);
    }, 50);
  };

  const handleCloseSnackbar = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleDownload = async (url: any) => {
    const originalImageUrl = url;
    const publicCORSProxyUrl = "https://api.allorigins.win/raw?url=";
    const proxiedImageUrl = publicCORSProxyUrl + encodeURIComponent(originalImageUrl);

    try {
      const imageResponse = await fetch(proxiedImageUrl, { mode: 'cors' });
      console.log('Content-Type:', imageResponse.headers.get('Content-Type'));

      if (!imageResponse.ok) {
        if (imageResponse.status === 304) {
          showSnackbar("Image is already cached and up-to-date. No new download occurred.", "info");
          return;
        }
        showSnackbar("Downloading failed. Status: " + imageResponse.status, "error");
        return;
      }

      const blob = await imageResponse.blob();
      const downloadLink = document.createElement('a');
      downloadLink.href = URL.createObjectURL(blob);
      downloadLink.download = "divine_quote.jpg";

      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);

      URL.revokeObjectURL(downloadLink.href);
      showSnackbar("Image downloaded successfully!", "success");

    } catch (err) {
      showSnackbar("Downloading failed: " + (err instanceof Error ? err.message : "An unknown error occurred."), "error");
      console.error("Download error:", err);
    }
  };

  // --- Swipe Handlers ---
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const distance = touchStartX.current - touchEndX.current;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && currentImageIndex < divineQuotes.length - 1) {
      setCurrentImageIndex(currentImageIndex + 1);
    } else if (isRightSwipe && currentImageIndex > 0) {
      setCurrentImageIndex(currentImageIndex - 1);
    }
  };
  // --- End Swipe Handlers ---

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        <HeaderBox>
          <IconButton onClick={() => navigate(-1)} sx={{ position: 'absolute', left: 16, top: 35, p: 0 }}>
            <ArrowBackIosIcon sx={{ color: "white", fontSize: '18px' }} />
          </IconButton>
          <IconButton onClick={() => navigate("/")} sx={{ position: 'absolute', left: 48, top: 35, p: 0 }}>
            <HomeOutlinedIcon sx={{ color: "white", fontSize: '24px' }} />
          </IconButton>
          <Typography variant="caption" sx={{ color: "#ffffffdd", opacity: 0.9, mb: 0.5 }}>
            {t("shrihari_tr")}
          </Typography>
          <Typography variant="h6" sx={{ color: "#ffffffdd", opacity: 0.9, fontSize: 15 }}>
            {t("Amrit_Vachan_tr")}
          </Typography>
        </HeaderBox>
        <Box
          sx={{
            backgroundColor: "whitesmoke",
            flexGrow: 1,
            overflow: 'hidden', // Hide overflow to prevent scrollbars during swipe
            paddingLeft: '0px',
            width: '100%',
            paddingBottom: `${FIXED_BOTTOM_BAR_HEIGHT + 16}px`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          // Attach touch event listeners to the image container
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <img
                src={divineQuotes?.[currentImageIndex]?.quotesThumbPath}
                style={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                }}
                alt={`Divine Quote ${currentImageIndex + 1}`}
              />
            </>
          )}
        </Box>
        <Box
          sx={{
            position: 'sticky',
            bottom: 40,
            width: '100%',
            zIndex: 100,
            backgroundColor: 'white',
            padding: '10px 0',
            display: 'flex',
            justifyContent: 'space-around',
            alignItems: 'center',
          }}
        >
          <Stack direction="row" spacing={2}>
            <IconButton
              onClick={() => { handleDownload(divineQuotes?.[currentImageIndex]?.quotesPath) 

                setShowDownloadPopup(true)
              }}
              sx={{
                border: '1px solid lightgray',
                color: 'gray',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <DownloadIcon />
            </IconButton>
            <IconButton
              sx={{
                border: '1px solid lightgray',
                color: 'gray',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <ShareIcon />
            </IconButton>
            <IconButton
              sx={{
                border: '1px solid lightgray',
                color: 'gray',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <WhatsAppIcon />
            </IconButton>
          </Stack>
          <Stack direction="row" spacing={2}>
            <IconButton
              onClick={() => {
                if (currentImageIndex > 0) setCurrentImageIndex(currentImageIndex - 1);
              }}
              disabled={currentImageIndex === 0}
            >
              <ArrowBackIcon />
            </IconButton>
            <IconButton
              onClick={() => {
                if (currentImageIndex < divineQuotes.length - 1) setCurrentImageIndex(currentImageIndex + 1);
              }}
              disabled={currentImageIndex === divineQuotes.length - 1}
            >
              <ArrowForwardIcon />
            </IconButton>
          </Stack>
        </Box>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={2000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbarSeverity}
            sx={{ width: '100%', borderRadius: '8px' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            width: '100%',
            zIndex: 100,
          }}
        >
          {/* <FixedBottomBar /> */}
        </Box>
      </Box>
    </>
  )
}

export default DivineQuotesImageview